import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GlassCard } from '../components/ui/GlassCard';
import { NeonButton } from '../components/ui/NeonButton';
import { LoaderIcon, UserIcon } from '../components/Icons';

interface LoginFormProps {
  onBack: () => void;
}

export default function LoginForm({ onBack }: LoginFormProps) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Backend only requires email for login (no password)
      await login(email, '');
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.message.includes('not found') || err.message.includes('404')) {
        setError('Account not found. Please check your email or create a new account.');
      } else if (err.message.includes('network')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Login failed. Please try again or contact support.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <GlassCard className="p-10 w-full max-w-md">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white mb-4 transition-colors"
        >
          ← Back
        </button>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <UserIcon className="text-cyan-400 h-12 w-12" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
          <p className="text-cyan-400/80">Sign in to continue your fitness journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-cyan-300/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
              placeholder="your.email@example.com"
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="pt-2">
            <NeonButton
              type="submit"
              disabled={loading}
              className="w-full py-3 text-lg flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <LoaderIcon className="h-5 w-5" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </NeonButton>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-cyan-300/20 text-center">
          <p className="text-sm text-gray-400">
            Demo System: Email-based authentication
          </p>
        </div>
      </GlassCard>
    </div>
  );
}
